'use client';

import Script from 'next/script';
import useGoogleHandler from '@alltrails/user-auth/hooks/useGoogleHandler';
import type LoginResponse from '@alltrails/user-auth/types/LoginResponse';
import logOneTapDisplayed from '@alltrails/amplitude/events/logOneTapDisplayed';
import useSetUser from '@alltrails/context/hooks/useSetUser';
import useUser from '@alltrails/context/hooks/useUser';
import { useEffect, useState } from 'react';
import { isAndroid } from '@alltrails/core';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { InstallBannerState } from '@alltrails/install-prompt/slices/installBanner';
import { useDispatch, useSelector } from 'react-redux';
import dynamic from 'next/dynamic';
import { setPlans } from '@alltrails/context/slices/plansSlice';
import { setCurrencyCode } from '@alltrails/context/slices/currencyCodeSlice';
import styles from './GoogleOneTap.module.scss';

const GenericPostAuthUpsellModal = dynamic(() => import('@alltrails/upsell/components/GenericPostAuthUpsell/PostAuthUpsellModal'), {
  ssr: false
});

type Props = {
  pageViewLimitReached?: boolean;
};

export default function GoogleOneTap({ pageViewLimitReached }: Props) {
  const googleHandler = useGoogleHandler();
  const setUser = useSetUser();
  const user = useUser();
  const isMobile = useIsMobileSizedScreen();
  const [isMounted, setIsMounted] = useState(false);
  const isInstallBannerOpen = useSelector((state: { installBanner: InstallBannerState }) => state.installBanner?.isOpen);
  const [didAuthWithOneTap, setDidAuthWithOneTap] = useState(false);
  const [didShowUpsell, setDidShowUpsell] = useState(false);
  const dispatch = useDispatch();

  function getShouldShowOneTap() {
    return isMounted && !user && (!isMobile || isAndroid());
  }

  const shouldShowOneTap = getShouldShowOneTap();

  const [isUpsellModalOpen, setIsUpsellModalOpen] = useState(false);

  async function callback(resp: any) {
    try {
      const response: LoginResponse = await googleHandler({ googleUserOrToken: resp.credential, handlerType: 'login', oneTap: true });
      setUser(response.user);
      dispatch(setPlans(response.plans));
      dispatch(setCurrencyCode(response.currencyCode));

      // Desktop upsell flow
      if (!isMobile && response.user.subscriptionTier !== 'peak') {
        setIsUpsellModalOpen(true);
      }

      setDidAuthWithOneTap(true);
      window.google?.accounts.id.cancel(); // google can be undefined on mobile web
    } catch {
      // we don't have a design spec for showing errors so we ignore them for now
    }
  }

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    // Mobile upsell flow (only show upsell after the banner is closed)
    const isBannerClosedOrUninitialized = !isInstallBannerOpen || isInstallBannerOpen === 'uninitialized';
    const isInUpsellTier = user?.subscriptionTier !== 'peak';
    const isEligibleForUpsell = isMobile && isBannerClosedOrUninitialized && didAuthWithOneTap && !didShowUpsell && isInUpsellTier;

    if (isEligibleForUpsell) {
      setIsUpsellModalOpen(true);
      setDidShowUpsell(true);
    }
  }, [isInstallBannerOpen, didAuthWithOneTap, didShowUpsell, isMobile, user?.subscriptionTier]);

  return (
    <>
      {shouldShowOneTap && (
        <>
          <div id="g_one_tap" className={styles.one} />
          <Script
            async
            onLoad={() => {
              window.google.accounts.id.initialize({
                callback,
                cancel_on_tap_outside: false,
                client_id: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID,
                prompt_parent_id: 'g_one_tap',
                use_fedcm_for_prompt: true
              });

              if (!pageViewLimitReached) {
                window.google.accounts.id.prompt((notification: any) => {
                  if (notification.isDisplayMoment() && notification.isDisplayed()) {
                    logOneTapDisplayed();
                  }
                });
              }
            }}
            src="https://accounts.google.com/gsi/client"
          />
        </>
      )}
      {isUpsellModalOpen && (
        <GenericPostAuthUpsellModal
          isOpen={isUpsellModalOpen}
          onCloseRequest={() => setIsUpsellModalOpen(false)}
          upsellTrigger="Complete Authentication"
        />
      )}
    </>
  );
}
